import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiTrendingUp,
    FiZap,
    FiTarget,
    FiPlus,
    FiArrowRight,
    FiAward,
    FiInfo
} from 'react-icons/fi';

const PredictorSection = () => {
    const [selectedSkill, setSelectedSkill] = useState<any>(null);

    const predictions = [
        {
            currentSkill: "UI Design",
            targetSkill: "3D Interaction Design",
            demand: "High",
            incomeImpact: "+45%",
            difficulty: "Medium",
            timeline: "4-6 weeks",
            why: "The rise of Virtual Workspaces has increased demand for spatial UI designers by 120% this quarter.",
            courses: ["Mastering Three.js", "Spatial UI Principles"]
        },
        {
            currentSkill: "Node.js",
            targetSkill: "Web3/Solidity",
            demand: "Surgent",
            incomeImpact: "+60%",
            difficulty: "Hard",
            timeline: "8-12 weeks",
            why: "TechUnity is expanding its escrow infrastructure. Decentralized finance skills are at a premium.",
            courses: ["Smart Contract Security", "Ethereum for Devs"]
        },
        {
            currentSkill: "React",
            targetSkill: "Micro-Frontend Architecture",
            demand: "Steady",
            incomeImpact: "+25%",
            difficulty: "Medium",
            timeline: "3-5 weeks",
            why: "Large enterprise projects on the platform are moving towards modular frontend systems.",
            courses: ["Scalable Module Fed", "Next.js Advanced"]
        }
    ];

    return (
        <section className="bg-[#1a1a1a] border border-premium-gold/20 rounded-2xl p-8 overflow-hidden relative">
            <div className="flex justify-between items-start mb-12">
                <div>
                    <h2 className="text-2xl font-serif font-bold text-premium-gold mb-2 flex items-center gap-2">
                        <FiZap />
                        AI Opportunity Predictor
                    </h2>
                    <p className="text-white/50 text-sm">Market-driven skill growth analysis.</p>
                </div>
                <div className="bg-premium-gold/10 px-3 py-1 rounded-full border border-premium-gold/30 text-[10px] text-premium-gold font-bold uppercase tracking-widest">
                    Real-time Analysis
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {predictions.map((pred, i) => (
                    <motion.div
                        key={i}
                        whileHover={{ y: -5, borderColor: 'rgba(212, 175, 55, 0.4)' }}
                        onClick={() => setSelectedSkill(pred)}
                        className={`cursor-pointer p-6 rounded-xl border transition-all ${selectedSkill === pred ? 'bg-premium-gold/10 border-premium-gold' : 'bg-white/5 border-white/10'}`}
                    >
                        <div className="flex justify-between items-start mb-4">
                            <span className="text-xs text-premium-gold font-bold">{pred.demand} Demand</span>
                            <FiPlus className="text-white/20" />
                        </div>
                        <h4 className="text-lg font-bold mb-1">{pred.targetSkill}</h4>
                        <p className="text-xs text-white/40 mb-4">Upgrade from {pred.currentSkill}</p>

                        <div className="flex items-center gap-2 text-green-400 font-bold text-sm">
                            <FiTrendingUp />
                            {pred.incomeImpact} Income Potential
                        </div>
                    </motion.div>
                ))}
            </div>

            <AnimatePresence mode="wait">
                {selectedSkill && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="bg-white/5 rounded-xl p-8 border border-white/10"
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            <div>
                                <h3 className="text-xl font-serif text-premium-gold mb-4 flex items-center gap-2">
                                    <FiAward />
                                    Strategic Analysis
                                </h3>
                                <p className="text-white/70 text-sm leading-relaxed mb-6">
                                    {selectedSkill.why}
                                </p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-black/40 p-3 rounded-lg border border-white/5">
                                        <p className="text-[10px] text-white/30 uppercase mb-1">Complexity</p>
                                        <p className="text-sm font-bold">{selectedSkill.difficulty}</p>
                                    </div>
                                    <div className="bg-black/40 p-3 rounded-lg border border-white/5">
                                        <p className="text-[10px] text-white/30 uppercase mb-1">Timeline</p>
                                        <p className="text-sm font-bold">{selectedSkill.timeline}</p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-xl font-serif text-white/90 mb-4 flex items-center gap-2">
                                    <FiTarget />
                                    Growth Path
                                </h3>
                                <div className="space-y-3">
                                    {selectedSkill.courses.map((course: string, idx: number) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-premium-gold/5 border border-premium-gold/20 rounded-lg group cursor-pointer hover:bg-premium-gold/10 transition-all">
                                            <span className="text-sm">{course}</span>
                                            <FiArrowRight className="text-premium-gold group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 flex items-start gap-2 text-xs text-white/30 italic">
                                    <FiInfo className="flex-shrink-0 mt-0.5" />
                                    <p>Our algorithms suggest this path based on your current 98.2% Trust Score and {selectedSkill.currentSkill} proficiency.</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Background elements */}
            <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
                <FiZap size={250} />
            </div>
        </section>
    );
};

export default PredictorSection;
